import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';
import * as Util from '../../../common/utilities';
import { withSession } from '../../../i18n/containers/AppInit.js';

import { Button, Input, Modal, Spinner } from '@salesforce/design-system-react';

import { FtpInfo, ImportSchedule, NotificationInfo } from '../';

import './DESelectionModal.scss';

class DESelectionModal extends Component {
  constructor(props) {
    super(props);
    this.modalName = 'profileDESelection';
  }

  // ===================================
  // =         Event Handlers          =
  // ===================================
  handleDataExtensionEditButton() {
    // close this modal and open the profile import wizard
    this.closeModal();
    this.props.onEdit();
  }

  handleScheduleChange(data) {
    this.props.actions.updateModal(this.modalName, {
      schedule: { ...this.currentSchedule(), ...data }
    });
  }

  handleFtpInfoChange(ftpFilename) {
    this.props.actions.updateModal(this.modalName, {
      file_name: ftpFilename
    });
  }

  handleNotificationEmailChange(email) {
    this.props.actions.updateModal(this.modalName, {
      notification_email: email
    });
  }

  handleNotifyOnSuccessToggle(notifyOnSuccess) {
    this.props.actions.updateModal(this.modalName, {
      notify_on_success: notifyOnSuccess
    });
  }

  handleNotifyOnErrorToggle(notifyOnError) {
    this.props.actions.updateModal(this.modalName, {
      notify_on_error: notifyOnError
    });
  }

  handleSaveAndSync(session) {
    return () => {
      this.syncAndSave(true, session);
    };
  }

  handleSave(session) {
    return () => {
      this.syncAndSave(false, session);
    };
  }

  isFTP() {
    return this.props?.flags?.ftpImport || this.props?.flags?.flags?.ftpImport;
  }

  syncAndSave(importNow, session) {
    const last = this.lastDataExtension();

    const importType = this.isFTP() ? 'FtpImport' : 'DeImport';
    const keyColumn =
      typeof last.subscriber_key === 'string'
        ? last.subscriber_key
        : last.subscriber_key.name;
    const de = {
      columns: last.import_columns,
      sendable_data_extension_field: { name: keyColumn },
      name: last.name,
      customer_key: last.customer_de_key
    };
    if (importType === 'DeImport') {
      if (de) {
        this.props.actions.createDataExtensionImport(
          de,
          this.currentSchedule(),
          this.currentNotificationInfo(),
          this.profileAttributes(),
          importNow,
          { cToken: session.cToken }
        );
      }
    } else if (importType === 'FtpImport') {
      if (de) {
        this.props.actions.createFtpImport(
          de,
          this.currentSchedule(),
          this.currentNotificationInfo(),
          this.currentFtpInfo(),
          this.profileAttributes(),
          false,
          { cToken: session.cToken }
        );
      }
    }
    this.closeModal();
  }

  // ===================================
  // =          Current Data           =
  // ===================================
  currentSchedule() {
    const current = this.props.modals[this.modalName]?.data?.schedule;
    if (current === undefined) {
      return this.previousSchedule();
    }

    return current.importRecurring ? current : {};
  }

  currentNotificationInfo() {
    const modal = this.props.modals[this.modalName];

    if (!modal.data) {
      return this.lastNotificationInfo();
    }

    return {
      email: this.notificationEmails().map(email => email.id),
      notify_on_success:
        modal.data.notify_on_success === undefined
          ? this.lastNotificationInfo().notify_on_success
          : modal.data.notify_on_success,
      notify_on_error:
        modal.data.notify_on_error === undefined
          ? this.lastNotificationInfo().notify_on_error
          : modal.data.notify_on_error
    };
  }

  currentFtpInfo() {
    const current = this.props.modals[this.modalName]?.data?.file_name;
    if (current === undefined) {
      return {
        file_name: this.props.importConfiguration?.data?.import_configuration
          ?.source_info?.file_name
      };
    }
    return { file_name: current };
  }

  profileAttributes() {
    const profileAttributes =
      this.props?.attributes?.data?.profileAttributes || [];
    if (profileAttributes.length > 0) {
      return profileAttributes;
    }
    return this.legacyProfileAttributes();
  }

  // TODO: remove this fallback after a reasonable amount of time
  legacyProfileAttributes() {
    const importColumns =
      this.props?.imports?.data?.lastDeImport?.source_info?.import_columns ||
      [];
    const enhancedImportColumns = importColumns.map(function(c) {
      const dataType = (c.data_type || c.field_type || 'text').toLowerCase();
      const values =
        dataType.toLowerCase() === 'boolean' ? ['True', 'False'] : undefined;
      return {
        name: c.name,
        data_type: dataType,
        values
      };
    });
    return enhancedImportColumns;
  }

  // ===================================
  // =         Previous Data           =
  // ===================================
  lastDataExtension() {
    const sourceInfo = this.props.importConfiguration?.data
      ?.import_configuration?.source_info;
    if (sourceInfo === undefined) {
      return undefined;
    } else {
      return {
        ...sourceInfo,
        customer_key: sourceInfo.customer_de_key,
        sendable_data_extension_field: { name: sourceInfo.subscriber_key }
      };
    }
  }

  lastNotificationInfo() {
    return (
      this.props.importConfiguration?.data?.import_configuration
        ?.notification_info || {}
    );
  }

  lastEmails() {
    return this.enhanceEmails(this.lastNotificationInfo()?.email);
  }

  lastSubscriberKey() {
    const sourceInfo = this.lastDataExtension();
    if (sourceInfo) {
      return typeof sourceInfo.subscriber_key === 'string'
        ? sourceInfo.subscriber_key
        : sourceInfo.subscriber_key.name;
    }
    return undefined;
  }

  previousSchedule() {
    const schedule = this.props.importConfiguration?.data?.import_configuration
      ?.schedule;
    if (schedule?.sync_days) {
      return { ...schedule, importRecurring: true };
    } else {
      return schedule;
    }
  }

  notificationEmails() {
    return (
      this.props.modals[this.modalName]?.data?.notification_email ||
      this.lastEmails() ||
      []
    );
  }

  enhanceEmails(emails) {
    if (!Array.isArray(emails)) {
      return [];
    } else {
      return emails.map(function(email) {
        return { id: email, label: email };
      });
    }
  }

  deName() {
    return this.lastDataExtension()?.name;
  }

  subscriberKey() {
    return (
      this.lastDataExtension()?.sendable_data_extension_field?.name ||
      this.lastSubscriberKey()
    );
  }

  getI18nID(suffix) {
    return `components.ecs.advancedSettings.dataExtensionSync.${suffix}`;
  }

  // ===================================
  // =           Actions               =
  // ===================================
  closeModal() {
    this.props.actions.closeModal(this.modalName);
  }

  // ===================================
  // =          Validation             =
  // ===================================
  validNotifications() {
    const info = this.currentNotificationInfo();
    if (
      !(info.email && info.email.length >= 1) &&
      (info.notify_on_success || info.notify_on_error)
    ) {
      return false;
    }
    return true;
  }

  saveAllowed() {
    // must have a DE selected (shouldn't be able to get to this modal without one)
    if (!this.lastDataExtension()) {
      return false;
    }

    // Don't allow save if notification is enabled but no address is entered
    if (!this.validNotifications()) {
      return false;
    }

    // Don't allow save while details are loading
    if (this.props.dataExtensions.isLoadingDetails) {
      return false;
    }

    return true;
  }

  // ===================================
  // =        React Lifecycle          =
  // ===================================
  componentDidUpdate(prevProps) {
    const isOpen = 'modals.profileDESelection.isOpen';
    const importType = this.isFTP() ? 'FtpImport' : 'DeImport';

    if (Util.propChanged(prevProps, this.props, isOpen, true)) {
      this.props.actions.getImportConfiguration(importType);
    }
  }

  // ===================================
  // =           Rendering             =
  // ===================================
  renderFooter(session) {
    const { formatMessage } = this.props.intl;
    /**
     * Cancel
     */
    const cancelLabel = formatMessage({
      id: this.getI18nID('selectionModal.actionButtons.cancel')
    });
    const cancelButton = (
      <Button
        label={cancelLabel}
        variant="neutral"
        onClick={this.closeModal.bind(this)}
      />
    );

    /**
     * Save
     */
    const saveLabel = formatMessage({
      id: this.getI18nID(`selectionModal.actionButtons.save`)
    });
    const saveButton = (
      <Button
        id="profileDESelectionSaveButton"
        label={saveLabel}
        disabled={!this.saveAllowed()}
        variant="brand"
        onClick={this.handleSave(session)}
      />
    );

    /**
     * Save and Sync Now
     */
    const saveAndSyncLabel = formatMessage({
      id: this.getI18nID(`selectionModal.actionButtons.saveAndSync`)
    });
    const saveAndSyncButton = (
      <Button
        id="profileDESelectionSaveButton"
        label={saveAndSyncLabel}
        disabled={!this.saveAllowed()}
        variant="brand"
        onClick={this.handleSaveAndSync(session)}
      />
    );

    return (
      <div className="footer-button-actions">
        <div className="footer-button-actions-left">{cancelButton}</div>
        <div className="footer-button-actions-right">
          {!this.isFTP() ? saveAndSyncButton : null}
          {saveButton}
        </div>
      </div>
    );
  }

  renderDataExtensionDetails() {
    const { formatMessage } = this.props.intl;
    const { flags } = this.props;
    const importConfiguration = this.props.importConfiguration?.data
      ?.import_configuration;

    if (
      this.props.dataExtensions.isLoadingDetails ||
      !this.props.importConfiguration?.isLoading === false
    ) {
      return (
        <div className="loading-details">
          <Spinner variant="brand" />
          <p>
            <FormattedMessage
              id={this.getI18nID('selectionModal.loadingDetails')}
            />
          </p>
        </div>
      );
    }
    return (
      <div className="slds-card-wrapper">
        <div className="slds-grid">
          <div className="data-extension-edit">
            <h2>
              <FormattedMessage
                id={this.getI18nID('selectionModal.changeTitle')}
              />
            </h2>
            <div className="data-extension-selector">
              <Input
                label={formatMessage({
                  id: this.getI18nID('selectionModal.labels.de')
                })}
                disabled
                value={this.deName()}
              />
              <Button
                id="profileDESelectionEditButton"
                label={formatMessage({
                  id: this.getI18nID('selectionModal.actionButtons.edit')
                })}
                variant="neutral"
                onClick={() => this.handleDataExtensionEditButton()}
              />
            </div>
            <div className="subscriber-key">
              <div className="label">
                <FormattedMessage
                  id={this.getI18nID('selectionModal.labels.pk')}
                />
              </div>
              <div>{this.subscriberKey()}</div>
            </div>
            {!importConfiguration?.last_initiated ? null : (
              <div className="last-sync-time">
                <div className="label">
                  <FormattedMessage
                    id={this.getI18nID('selectionModal.labels.lastSync')}
                  />
                </div>
                <div>
                  {formatTimestamp(this.props.intl)(
                    importConfiguration?.last_initiated
                  )}
                </div>
              </div>
            )}
            <div className="import-settings-container">
              <ImportSchedule
                formChange={this.handleScheduleChange.bind(this)}
                schedule={this.currentSchedule()}
              />
              <FtpInfo
                ftpFilename={this.currentFtpInfo()}
                handleFtpInfoChange={this.handleFtpInfoChange.bind(this)}
                flags={flags}
              />
              <NotificationInfo
                email={this.notificationEmails()}
                notifyOnSuccess={
                  this.currentNotificationInfo().notify_on_success
                }
                notifyOnError={this.currentNotificationInfo().notify_on_error}
                handleNotificationEmailChange={this.handleNotificationEmailChange.bind(
                  this
                )}
                handleNotifyOnSuccessToggle={this.handleNotifyOnSuccessToggle.bind(
                  this
                )}
                handleNotifyOnErrorToggle={this.handleNotifyOnErrorToggle.bind(
                  this
                )}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderModal() {
    return this.renderDataExtensionDetails();
  }

  render() {
    const { formatMessage } = this.props.intl;
    const isOpen =
      this.props.modals[this.modalName] &&
      this.props.modals[this.modalName].isOpen;
    const modalTitle = formatMessage({
      id: this.getI18nID('selectionModal.title')
    });

    if (!isOpen) {
      return null;
    }

    return withSession(session => {
      return (
        <Modal
          heading={modalTitle}
          footer={this.renderFooter(session)}
          onRequestClose={this.closeModal.bind(this)}
          dismissOnClickOutside={false}
          disableClose={true}
          isOpen={isOpen || false}
        >
          {this.renderModal()}
        </Modal>
      );
    });
  }
}

DESelectionModal.propTypes = {
  actions: PropTypes.object.isRequired,
  modals: PropTypes.object.isRequired,
  attributes: PropTypes.object.isRequired,
  dataExtensions: PropTypes.object.isRequired,
  rules: PropTypes.object.isRequired,
  imports: PropTypes.object.isRequired,
  importConfiguration: PropTypes.object.isRequired,
  onEdit: PropTypes.func,
  flags: PropTypes.object.isRequired,
  intl: intlShape.isRequired
};

export default injectIntl(DESelectionModal);
